import type {CSSProperties} from 'react';
import {WEDGE_BRIEF_CINE} from '@territory/tokens';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeBriefCineLook = 'brief-cine-skip' | 'brief-cine-engage';

const BRIEF_CINE_LOOKS = new Set<string>(['brief-cine-skip', 'brief-cine-engage']);
export const isWedgeBriefCineLook = (look: string | undefined): look is WedgeBriefCineLook => look !== undefined && BRIEF_CINE_LOOKS.has(look);

/* Released buttons of the first sample brief mission briefing, a fixed dark scene. The skip look is a pill pinned top right under the
   safe area: no fill, a half pixel slate edge, 11.5px semibold slate type in the Wedge mono face, 0.6 tracking, uppercase, 8px 14px of
   padding and a stacking order of 3. The engage look is the emerald call to action: an emerald fill with the scene ink, no edge, 14px
   28px of padding, a 14px radius, 13px heavy mono uppercase type, 1.2 tracking and a 10px gap; the caller names the animations in its
   sheet. Neither draws a transparent target or an anchor (wedgeSettingsDrawsHit): the skip pill and the engage pad keep legacy geometry. */
export function wedgeBriefCineStyle(look: WedgeBriefCineLook): CSSProperties {
  const C = WEDGE_BRIEF_CINE;
  switch (look) {
    case 'brief-cine-skip': return {
      position: 'absolute', top: 'calc(env(safe-area-inset-top, 0px) + 40px)', right: 14,
      background: 'transparent', border: `0.5px solid ${C.slate20}`,
      color: C.slate70, fontSize: 11.5, fontWeight: 600,
      padding: '8px 14px', borderRadius: 999, cursor: 'pointer',
      fontFamily: wedgeExactRole('mono'),
      letterSpacing: 0.6, textTransform: 'uppercase',
      touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
      zIndex: 3,
    };
    case 'brief-cine-engage': return {
      background: C.emerald, color: C.bg, border: 0,
      padding: '14px 28px', borderRadius: 14,
      fontFamily: wedgeExactRole('mono'),
      fontSize: 13, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase',
      cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 10,
      touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
      animation: `prepCineCta 720ms ${wedgeExactRole('ease')} both, prepCineCtaPulse 2.6s ease-in-out 720ms infinite`,
    };
  }
}
